import WhatsAppAccountHealthBadge from "./WhatsAppAccountHealthBadge";
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Avatar,
  Button,
  Modal,
  message,
  Input,
  Form,
  Radio,
  Select,
  Tag,
  Empty,
  Tooltip,
  Breadcrumb,
  Dropdown,
} from "antd";
import {
  ArrowLeftOutlined,
  MessageOutlined,
  StarFilled,
  UserOutlined,
  UserAddOutlined,
  DisconnectOutlined,
  BugOutlined,
  SyncOutlined,
  PlusOutlined,
  DeleteOutlined,
  WarningOutlined,
  TagOutlined,
  BranchesOutlined,
  EllipsisOutlined,
  CalendarOutlined,
  CopyOutlined,
  PushpinOutlined,
  PushpinFilled,
  InboxOutlined,
  LockOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import {
  deleteAuthMethod,
  postAuthMethod,
  getAuthMethod,
  patchAuthMethod,
} from "../../services/HttpServices";
import ChatListSidebar from "./inbox/ChatListSidebar";
import ChatWindow from "./inbox/ChatWindow";
import moment from "moment";
import LeadDetailSidebar from "./inbox/LeadDetailSidebar";
import UserInitialsAvatar from "./inbox/UserInitialsAvatar";
import { PanelRightClose, PanelRightOpen } from "lucide-react";
import WhatsAppTemplateComposer from "./template-composer/WhatsAppTemplateComposer";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import hotel from "../../assets/svg/hotel.svg";
import FeatureComingSoon from "../../common/FeatureComingSoon";
import whatsappWebSocketService from "../../services/whatsappWebSocketService";
import { getUserInfo } from "../../utils/utils";
import { formatWhatsAppChatTime } from "../../utils";
import { isAdminUser } from "../../utils/teamAccess";
import CreateNewLead from "../lead-search/CreateNewLead";

const { Option } = Select;

const WhatsAppInbox = ({ onOpenCampaigns, connectionData, onDisconnect }) => {
  const navigate = useNavigate();
  const [activeChatId, setActiveChatId] = useState(null); // Will hold conversationId
  const activeChatIdRef = useRef(activeChatId);
  const [chatsLoaded, setChatsLoaded] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [hasProcessedUrlParams, setHasProcessedUrlParams] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);
  const LOCAL_STORAGE_PHONE_KEY = "whatsapp_selected_phone_id";
  const LOCAL_STORAGE_WABA_KEY = "whatsapp_selected_waba_id";

  const getInitialPhoneId = () => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_PHONE_KEY);
      if (
        saved &&
        connectionData?.phoneNumbers?.some(
          (p) => String(p.id) === String(saved),
        )
      ) {
        return saved;
      }
    } catch (e) {
      console.error(e);
    }
    return connectionData?.phoneNumbers?.length > 0
      ? connectionData.phoneNumbers[0].id
      : null;
  };

  const getInitialWabaId = () => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_WABA_KEY);
      if (saved) return saved;
    } catch (e) {}
    const activePhone = connectionData?.phoneNumbers?.find(
      (p) => String(p.id) === String(getInitialPhoneId()),
    );
    return (
      activePhone?.wabaId ||
      activePhone?.whatsappBusinessAccountId ||
      connectionData?.wabaId ||
      connectionData?.id ||
      1
    );
  };

  const [selectedPhoneId, setSelectedPhoneId] = useState(getInitialPhoneId);
  const [selectedWabaId, setSelectedWabaId] = useState(getInitialWabaId);
  const [localConnectionData, setLocalConnectionData] =
    useState(connectionData);

  const handleSelectPhone = (phoneId, wabaId) => {
    if (!phoneId && !wabaId) return;
    if (phoneId) {
      setSelectedPhoneId(phoneId);
      try {
        localStorage.setItem(LOCAL_STORAGE_PHONE_KEY, phoneId);
      } catch (e) {
        console.error(e);
      }
    }
    const resolvedWabaId =
      wabaId ||
      (localConnectionData || connectionData)?.phoneNumbers?.find(
        (p) => String(p.id) === String(phoneId),
      )?.wabaId ||
      (localConnectionData || connectionData)?.phoneNumbers?.find(
        (p) => String(p.id) === String(phoneId),
      )?.whatsappBusinessAccountId ||
      (localConnectionData || connectionData)?.wabaId ||
      (localConnectionData || connectionData)?.id ||
      1;

    if (resolvedWabaId) {
      setSelectedWabaId(resolvedWabaId);
      try {
        localStorage.setItem(LOCAL_STORAGE_WABA_KEY, resolvedWabaId);
      } catch (e) {}
    }
    setActiveChatId(null);
  };

  useEffect(() => {
    setLocalConnectionData(connectionData);
    if (connectionData?.phoneNumbers?.length > 0) {
      setSelectedPhoneId((prev) => {
        try {
          const saved = localStorage.getItem(LOCAL_STORAGE_PHONE_KEY);
          const savedPhone = connectionData.phoneNumbers.find(
            (p) => String(p.id) === String(saved),
          );
          if (saved && savedPhone) {
            return savedPhone.id;
          }
        } catch (e) {}

        const prevPhone = connectionData.phoneNumbers.find(
          (p) => String(p.id) === String(prev),
        );
        if (prev && prevPhone) {
          try {
            localStorage.setItem(LOCAL_STORAGE_PHONE_KEY, prevPhone.id);
          } catch (e) {}
          return prevPhone.id;
        }

        const defaultId = connectionData.phoneNumbers[0].id;
        try {
          localStorage.setItem(LOCAL_STORAGE_PHONE_KEY, defaultId);
        } catch (e) {}
        return defaultId;
      });
    }

    const currentWaba =
      connectionData?.wabaId ||
      connectionData?.id ||
      connectionData?.phoneNumbers?.[0]?.wabaId ||
      1;
    setSelectedWabaId((prev) => {
      try {
        const saved = localStorage.getItem(LOCAL_STORAGE_WABA_KEY);
        if (saved) return saved;
      } catch (e) {}
      if (prev) return prev;
      try {
        localStorage.setItem(LOCAL_STORAGE_WABA_KEY, currentWaba);
      } catch (e) {}
      return currentWaba;
    });
  }, [connectionData]);
  const [chats, setChats] = useState([]);
  const chatsRef = useRef(chats);
  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);
  const [chatPage, setChatPage] = useState(0);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [messagesData, setMessagesData] = useState({}); // { customerPhone: [messages] }
  const [mobileView, setMobileView] = useState("list");
  const [mobilePanel, setMobilePanel] = useState("chat");
  const [isLeadDetailCollapsed, setIsLeadDetailCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem("wa_lead_detail_collapsed");
      return saved !== null ? saved === "true" : true;
    } catch (e) {
      return true;
    }
  });

  const toggleLeadDetail = () => {
    setIsLeadDetailCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("wa_lead_detail_collapsed", String(next));
      } catch (e) {}
      return next;
    });
    if (mobilePanel === "details") {
      setMobilePanel("chat");
    }
  };
  const stompClientRef = useRef(null);

  const userInfo = getUserInfo();
  const accountId = userInfo?.account?.id || userInfo?.login;
  const isAdmin = isAdminUser(userInfo);
  const hasViewAll = userInfo?.authorities?.includes("leads.view_all");
  const hasViewTeam = userInfo?.authorities?.includes("leads.view_team");
  const canViewUnknownLeads = isAdmin || hasViewAll || hasViewTeam;

  const [isTestModalVisible, setIsTestModalVisible] = useState(false);
  const [isNewChatModalVisible, setIsNewChatModalVisible] = useState(false);
  const [leadSearchQuery, setLeadSearchQuery] = useState("");
  const [leadSearchResults, setLeadSearchResults] = useState([]);
  const [searchingLeads, setSearchingLeads] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [newChatPhoneId, setNewChatPhoneId] = useState(null);

  const leadOptionsToRender = useMemo(() => {
    if (
      selectedLead &&
      !leadSearchResults.some((l) => l.id === selectedLead.id)
    ) {
      return [selectedLead, ...leadSearchResults];
    }
    return leadSearchResults;
  }, [selectedLead, leadSearchResults]);

  useEffect(() => {
    fetchTemplates();
  }, []);
  const [testPhone, setTestPhone] = useState("");
  const [testBody, setTestBody] = useState("");
  const [messageType, setMessageType] = useState("TEMPLATE");
  const [selectedTemplate, setSelectedTemplate] = useState(null); // stores the full template object
  const [testLoading, setTestLoading] = useState(false);
  const [wamid, setWamid] = useState("");
  const [messageStatus, setMessageStatus] = useState(null);

  const [globalSearchQuery, setGlobalSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(globalSearchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [globalSearchQuery]);

  useEffect(() => {
    if ((selectedPhoneId || selectedWabaId) && connectionData) {
      setChats([]);
      setHasMoreChats(true);
      fetchChats(0);
    }
  }, [selectedPhoneId, selectedWabaId, connectionData, debouncedSearchQuery]);

  const updateChatClaimState = (conversationId, apiChat) => {
    setChats((prevChats) =>
      prevChats.map((c) =>
        c.id === conversationId
          ? {
              ...c,
              claimedByUserId: apiChat.claimedByUserId,
              claimedAt: apiChat.claimedAt,
            }
          : c,
      ),
    );
  };

  const handleClaimConversation = async (conversationId) => {
    try {
      const response = await postAuthMethod(
        `/api/whatsapp-conversations/${conversationId}/claim`,
      );
      if (response && response.data) {
        updateChatClaimState(conversationId, response.data);
        message.success("Conversation claimed successfully");
      }
    } catch (error) {
      console.error("Claim error:", error);
      message.error(
        error.response?.data?.title || "Failed to claim conversation",
      );
    }
  };

  const handleReleaseClaim = async (conversationId) => {
    try {
      const response = await postAuthMethod(
        `/api/whatsapp-conversations/${conversationId}/release-claim`,
      );
      if (response && response.data) {
        updateChatClaimState(conversationId, response.data);
        message.success("Claim released successfully");
      }
    } catch (error) {
      console.error("Release claim error:", error);
      message.error(error.response?.data?.title || "Failed to release claim");
    }
  };

  const handleOverrideClaim = async (conversationId) => {
    try {
      const response = await postAuthMethod(
        `/api/whatsapp-conversations/${conversationId}/override-claim`,
      );
      if (response && response.data) {
        updateChatClaimState(conversationId, response.data);
        message.success("Claim overridden successfully");
      }
    } catch (error) {
      console.error("Override claim error:", error);
      message.error(
        error.response?.data?.title ||
          "Failed to override claim. Ensure you have proper permissions.",
      );
    }
  };

  const fetchLastMessageForChat = (chat) => {
    if (!chat || !chat.phone) return;
    const phone = chat.phone;
    let url = `/api/whatsapp/messages?customerPhone=${encodeURIComponent(phone)}&page=0&size=1&sort=metaTimestamp,desc&sort=id,desc`;
    if (chat.whatsappPhoneNumberId) {
      url += `&whatsappPhoneNumberId=${chat.whatsappPhoneNumberId}`;
    }

    getAuthMethod(url)
      .then((res) => {
        const lastMsg = res.data && res.data[0];
        if (lastMsg) {
          setChats((prev) => {
            const targetChatIndex = prev.findIndex(
              (c) =>
                c.id === chat.id ||
                c.phone === phone ||
                (c.phone &&
                  phone &&
                  c.phone.replace(/\D/g, "") === phone.replace(/\D/g, "")),
            );
            if (targetChatIndex > -1) {
              const isOutbound = lastMsg.direction === "OUTBOUND";
              const lastMsgTime =
                lastMsg.metaTimestamp || lastMsg.webhookReceivedAt;

              let msgContent = lastMsg.messageBody || "";
              if (!msgContent && lastMsg.mediaType) {
                msgContent = `[${lastMsg.mediaType.charAt(0).toUpperCase() + lastMsg.mediaType.slice(1).toLowerCase()}]`;
              }

              const updatedChat = {
                ...prev[targetChatIndex],
                message: msgContent || prev[targetChatIndex].message,
                lastMessageSender: isOutbound
                  ? "You"
                  : prev[targetChatIndex].name || "Customer",
                time: lastMsgTime
                  ? formatWhatsAppChatTime(lastMsgTime)
                  : prev[targetChatIndex].time,
                lastMessageTime: lastMsgTime
                  ? new Date(lastMsgTime).getTime()
                  : prev[targetChatIndex].lastMessageTime,
              };
              const updatedChats = prev.map((c, i) =>
                i === targetChatIndex ? updatedChat : c,
              );
              return [...updatedChats].sort(
                (a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0),
              );
            }
            return prev;
          });
        }
      })
      .catch((err) =>
        console.error("Failed to fetch last message for " + phone, err),
      );
  };

  const fetchChats = (page = 0) => {
    let url = `/api/whatsapp-conversations?page=${page}&size=20&sort=lastMessageTime,desc`;
    const wabaToUse =
      selectedWabaId ||
      (localConnectionData || connectionData)?.phoneNumbers?.find(
        (p) => String(p.id) === String(selectedPhoneId),
      )?.wabaId ||
      (localConnectionData || connectionData)?.phoneNumbers?.find(
        (p) => String(p.id) === String(selectedPhoneId),
      )?.whatsappBusinessAccountId ||
      (localConnectionData || connectionData)?.wabaId ||
      (localConnectionData || connectionData)?.id ||
      1;

    if (wabaToUse) {
      url += `&wabaId.equals=${wabaToUse}`;
    }
    if (debouncedSearchQuery) {
      url += `&globalSearch.contains=${encodeURIComponent(debouncedSearchQuery)}`;
    }

    getAuthMethod(url)
      .then((res) => {
        const rawList = res.data || [];
        const newChats = rawList.map((conv) => {
          const lastMsgText =
            conv.lastMessageMessageBody ||
            conv.lastMessageBody ||
            conv.lastMessageText ||
            conv.lastMessageContent ||
            conv.lastMessageMessage ||
            conv.message ||
            conv.messageBody ||
            (typeof conv.lastMessage === "string"
              ? conv.lastMessage
              : conv.lastMessage?.messageBody ||
                conv.lastMessage?.text ||
                conv.lastMessage?.body ||
                "");
          const isOutbound =
            conv.lastMessageDirection === "OUTBOUND" ||
            conv.lastMessage?.direction === "OUTBOUND" ||
            conv.lastMessageSender === "AGENT" ||
            conv.lastMessageSender === "SYSTEM" ||
            conv.lastMessageSender === "OUTBOUND";
          const lastMsgSender = lastMsgText
            ? isOutbound
              ? "You"
              : conv.lastMessageWhatsappProfileName ||
                conv.lastMessageSenderName ||
                conv.whatsappProfileName ||
                conv.customerPhone ||
                "Customer"
            : "";

          return {
            id: conv.id,
            conversationId: conv.id,
            wabaId: conv.wabaId,
            name:
              conv.whatsappProfileName ||
              conv.clientLeadName ||
              conv.customerPhone,
            clientLeadName: conv.clientLeadName,
            whatsappProfileName: conv.whatsappProfileName,
            phone: conv.customerPhone,
            whatsappPhoneNumberId: conv.whatsappPhoneNumberId,
            message: lastMsgText || "",
            lastMessageSender: lastMsgSender || "",
            time: formatWhatsAppChatTime(conv.lastMessageTime),
            lastMessageTime: conv.lastMessageTime
              ? new Date(conv.lastMessageTime).getTime()
              : 0,
            unread: conv.unreadCount || 0,
            unreadCount: conv.unreadCount || 0,
            status: conv.status,
            serviceWindowExpiresAt: conv.serviceWindowExpiresAt,
            isPinned: conv.isPinned || false,
            isArchived: conv.isArchived || false,
            assignedUserId: conv.assignedUserId,
            claimedByUserId: conv.claimedByUserId,
            claimedAt: conv.claimedAt,
            avatar: conv.avatarUrl || conv.avatar || null,
            // Lead Data mapping
            clientLeadId: conv.clientLeadId,
            email: conv.clientLeadEmail || "N/A",
            stage: conv.clientLeadStatusName || "No Status",
            rating: conv.clientLeadRatingText || "No Rating",
            value: conv.clientLeadOpportunityAmount || "0",
          };
        });

        setHasMoreChats(newChats.length === 20);

        if (activeChatIdRef.current) {
          const matchingNewChat = newChats.find(
            (c) =>
              c.phone === activeChatIdRef.current ||
              c.phone?.replace(/^\+/, "") ===
                activeChatIdRef.current?.toString()?.replace(/^\+/, ""),
          );
          if (
            matchingNewChat &&
            matchingNewChat.id !== activeChatIdRef.current
          ) {
            const oldId = activeChatIdRef.current;
            const newId = matchingNewChat.id;
            setActiveChatId(newId);
            setMessagesData((prevMsgs) => {
              if (prevMsgs[oldId]) {
                const updated = { ...prevMsgs };
                updated[newId] = updated[oldId];
                delete updated[oldId];
                return updated;
              }
              return prevMsgs;
            });
          }
        }

        if (page === 0) {
          const sortedChats = [...newChats].sort(
            (a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0),
          );
          setChats(sortedChats);
          sortedChats.forEach((chat) => fetchLastMessageForChat(chat));
        } else {
          setChats((prev) => {
            const combined = [...prev, ...newChats];
            return combined.sort(
              (a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0),
            );
          });
          newChats.forEach((chat) => fetchLastMessageForChat(chat));
        }
      })
      .catch((err) => console.error("Failed to fetch chats", err))
      .finally(() => {
        setChatsLoaded(true);
      });
  };

  useEffect(() => {
    if (chatsLoaded && !hasProcessedUrlParams) {
      const leadPhone = searchParams.get("phone");
      const leadName = searchParams.get("name");
      const leadId = searchParams.get("leadId");

      if (leadPhone) {
        setHasProcessedUrlParams(true);

        // Remove query parameters from URL so they don't trigger again
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("phone");
        newParams.delete("name");
        newParams.delete("leadId");
        setSearchParams(newParams, { replace: true });

        // Check if chat already exists
        const cleanedPhone = leadPhone.trim().replace(/^\+/, "");
        const parsedLeadId = leadId ? parseInt(leadId) : null;
        const existingChat = chatsRef.current.find(
          (c) =>
            c.phone?.trim().replace(/^\+/, "") === cleanedPhone ||
            c.id === leadPhone ||
            (parsedLeadId && c.clientLeadId === parsedLeadId),
        );

        if (existingChat) {
          handleSelectChat(existingChat.id);
        } else {
          // Inject new chat into list
          const newChat = {
            id: leadPhone,
            conversationId: null, // it will get created on first message
            name: leadName || leadPhone,
            clientLeadName: leadName,
            clientLeadId: leadId ? parseInt(leadId) : undefined,
            whatsappProfileName: leadName,
            phone: leadPhone,
            message: "",
            time: formatWhatsAppChatTime(new Date()),
            lastMessageTime: new Date().getTime(),
            unread: 0,
            status: "New",
            avatar: null,
            whatsappPhoneNumberId:
              selectedPhoneId || localConnectionData?.phoneNumbers?.[0]?.id,
            email: "N/A",
            stage: "New Lead",
            rating: "No Rating",
            value: "0",
          };
          setChats((prevChats) =>
            [newChat, ...prevChats].sort(
              (a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0),
            ),
          );
          handleSelectChat(newChat.id);
        }
      }
    }
  }, [
    chatsLoaded,
    searchParams,
    hasProcessedUrlParams,
    selectedPhoneId,
    localConnectionData,
  ]);

  const isChatBelongingToSelectedPhone = (
    chat,
    phoneId,
    activePhoneObj,
    totalPhones,
  ) => {
    if (!phoneId) return true;
    if (chat.whatsappPhoneNumberId) {
      return String(chat.whatsappPhoneNumberId) === String(phoneId);
    }
    const cleanSelected = String(
      activePhoneObj?.displayPhoneNumber || activePhoneObj?.phoneNumber || "",
    ).replace(/\D/g, "");
    const cleanChatPhone = String(
      chat.businessPhoneNumber ||
        chat.phoneNumber ||
        chat.whatsappPhoneNumber ||
        "",
    ).replace(/\D/g, "");
    if (cleanChatPhone && cleanSelected) {
      return cleanChatPhone === cleanSelected;
    }
    return (totalPhones || 0) <= 1;
  };

  const selectedAccountChats = useMemo(() => {
    const activePhoneObj = (
      localConnectionData || connectionData
    )?.phoneNumbers?.find((p) => String(p.id) === String(selectedPhoneId));
    const totalPhones =
      (localConnectionData || connectionData)?.phoneNumbers?.length || 0;
    return chats.filter((chat) =>
      isChatBelongingToSelectedPhone(
        chat,
        selectedPhoneId,
        activePhoneObj,
        totalPhones,
      ),
    );
  }, [chats, selectedPhoneId, localConnectionData, connectionData]);

  // Default auto-select first chat of the selected account when chats load or when active account changes
  useEffect(() => {
    if (!chatsLoaded) return;
    const leadPhone = searchParams.get("phone");
    if (leadPhone) return;

    // Check if current activeChatId belongs to the selected account chats
    const currentActiveBelongs =
      activeChatId && selectedAccountChats.some((c) => c.id === activeChatId);

    if (!currentActiveBelongs) {
      if (selectedAccountChats.length > 0) {
        // Prioritize pinned chats if any, exactly matching the top item of the sidebar list
        const firstChat =
          selectedAccountChats.find((c) => c.isPinned) ||
          selectedAccountChats[0];
        handleSelectChat(firstChat.id);
      } else {
        setActiveChatId(null);
      }
    }
  }, [chatsLoaded, selectedAccountChats, activeChatId, searchParams]);

  const [messagePages, setMessagePages] = useState({}); // { customerPhone: page }
  const [hasMoreMessages, setHasMoreMessages] = useState({}); // { customerPhone: boolean }
  const [messageErrors, setMessageErrors] = useState({}); // { customerPhone: string }

  const fetchMessages = (chatId, page = 0) => {
    const activeChat = chats.find((c) => c.id === chatId);
    if (!activeChat) return;

    let url = `/api/whatsapp/messages?customerPhone=${encodeURIComponent(activeChat.phone)}&page=${page}&size=50&sort=metaTimestamp,desc&sort=id,desc`;
    if (activeChat.whatsappPhoneNumberId) {
      url += `&whatsappPhoneNumberId=${activeChat.whatsappPhoneNumberId}`;
    }

    getAuthMethod(url)
      .then((res) => {
        const msgs = (res.data || []).map((msg) => ({
          id: msg.wamid,
          sender: msg.direction === "INBOUND" ? "user" : "bot",
          text: msg.messageBody,
          time: new Date(
            msg.metaTimestamp || msg.webhookReceivedAt,
          ).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          timestamp: msg.metaTimestamp || msg.webhookReceivedAt,
          status: msg.status,
          mediaType: msg.mediaType,
          mediaUrl: msg.mediaUrl,
          mediaFileName: msg.mediaFileName,
          mediaStatus: msg.mediaStatus,
          mediaAssetId: msg.mediaAssetId,
        }));

        setHasMoreMessages((prev) => ({
          ...prev,
          [chatId]: msgs.length === 50,
        }));
        setMessagePages((prev) => ({ ...prev, [chatId]: page }));

        // msgs are newest first from backend. We want oldest at top.
        const reversedMsgs = [...msgs].reverse();

        if (page === 0) {
          setMessagesData((prev) => ({ ...prev, [chatId]: reversedMsgs }));

          if (msgs.length > 0) {
            const latestMsg = msgs[0];
            const isOutbound = latestMsg.sender === "bot";
            let latestText = latestMsg.text;
            if (!latestText && latestMsg.mediaType) {
              latestText = `[${latestMsg.mediaType.charAt(0).toUpperCase() + latestMsg.mediaType.slice(1).toLowerCase()}]`;
            }
            setChats((prev) =>
              prev.map((c) => {
                if (c.id === chatId) {
                  return {
                    ...c,
                    message: latestText || c.message,
                    lastMessageSender: isOutbound
                      ? "You"
                      : c.name || "Customer",
                    time: latestMsg.timestamp
                      ? formatWhatsAppChatTime(latestMsg.timestamp)
                      : c.time,
                    lastMessageTime: latestMsg.timestamp
                      ? new Date(latestMsg.timestamp).getTime()
                      : c.lastMessageTime,
                  };
                }
                return c;
              }),
            );
          }
        } else {
          setMessagesData((prev) => ({
            ...prev,
            [chatId]: [...reversedMsgs, ...(prev[chatId] || [])],
          }));
        }
      })
      .catch((err) => console.error("Failed to fetch messages", err));
  };

  const loadMoreChats = () => {
    if (!hasMoreChats) return;
    const nextPage = chatPage + 1;
    setChatPage(nextPage);
    fetchChats(nextPage);
  };

  useEffect(() => {
    if (!isNewChatModalVisible) {
      setLeadSearchResults([]);
      return;
    }

    const delayDebounceFn = setTimeout(() => {
      setSearchingLeads(true);
      const url = leadSearchQuery.trim()
        ? `/api/client-leads?size=20&term=${encodeURIComponent(leadSearchQuery)}`
        : `/api/client-leads?size=20`;

      getAuthMethod(url)
        .then((res) => {
          const results = (res.data || []).filter(
            (lead) => !!lead.phoneNo || !!lead.mobile,
          );
          // Limit to 5 items if the search query is empty (default suggestions)
          const slicedResults = leadSearchQuery.trim()
            ? results
            : results.slice(0, 5);
          setLeadSearchResults(slicedResults);
        })
        .catch((err) => console.error("Failed to fetch leads", err))
        .finally(() => setSearchingLeads(false));
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [leadSearchQuery, isNewChatModalVisible]);

  useEffect(() => {
    if (activeChatId) {
      fetchMessages(activeChatId);
    }
  }, [activeChatId]);

  useEffect(() => {
    if (!accountId) return;

    let messageSub = null;
    let statusSub = null;
    let conversationSub = null;
    let conversationDeletedSub = null;

    whatsappWebSocketService
      .connectWS()
      .then((client) => {
        stompClientRef.current = client;

        messageSub = whatsappWebSocketService.subscribe(
          `/topic/whatsapp/${accountId}/messages`,
          (messageBody) => {
            const event = JSON.parse(messageBody);
            if (event.eventType === "MESSAGE_CREATED") {
              const newMsg = {
                id: event.id || event.wamid,
                sender: event.direction === "INBOUND" ? "user" : "bot",
                text: event.messageBody,
                time: new Date(event.timestamp).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
                timestamp: event.timestamp,
                status: event.status,
                mediaType: event.mediaType,
                mediaUrl: event.mediaUrl,
                mediaFileName: event.mediaFileName,
                mediaStatus: event.mediaStatus,
                mediaAssetId: event.mediaAssetId,
              };

              const targetChat = chatsRef.current.find(
                (c) =>
                  c.phone === event.customerPhone &&
                  c.whatsappPhoneNumberId === event.whatsappPhoneNumberId,
              );

              if (targetChat) {
                setMessagesData((prev) => ({
                  ...prev,
                  [targetChat.id]: [...(prev[targetChat.id] || []), newMsg],
                }));

                setChats((prev) => {
                  const targetChatIndex = prev.findIndex(
                    (chat) => chat.id === targetChat.id,
                  );
                  if (targetChatIndex > -1) {
                    const isOutbound = event.direction === "OUTBOUND";
                    const updatedChat = {
                      ...prev[targetChatIndex],
                      message: event.messageBody,
                      lastMessageSender: isOutbound
                        ? "You"
                        : prev[targetChatIndex].name || "Customer",
                      time: formatWhatsAppChatTime(event.timestamp),
                      lastMessageTime: event.timestamp
                        ? new Date(event.timestamp).getTime()
                        : prev[targetChatIndex].lastMessageTime,
                    };
                    const filtered = prev.filter(
                      (_, idx) => idx !== targetChatIndex,
                    );
                    return [updatedChat, ...filtered].sort(
                      (a, b) =>
                        (b.lastMessageTime || 0) - (a.lastMessageTime || 0),
                    );
                  }
                  return prev;
                });
              }
            } else if (event.eventType === "MESSAGE_DELETED") {
              const targetChat = chatsRef.current.find(
                (c) =>
                  c.phone === event.customerPhone &&
                  c.whatsappPhoneNumberId === event.whatsappPhoneNumberId,
              );
              if (targetChat) {
                setMessagesData((prev) => ({
                  ...prev,
                  [targetChat.id]: (prev[targetChat.id] || []).filter(
                    (msg) => msg.id !== event.wamid,
                  ),
                }));
              }
            }
          },
        );

        statusSub = whatsappWebSocketService.subscribe(
          `/topic/whatsapp/${accountId}/statuses`,
          (messageBody) => {
            const event = JSON.parse(messageBody);
            if (event.eventType === "STATUS_UPDATED") {
              setMessagesData((prev) => {
                const next = { ...prev };
                Object.keys(next).forEach((chatId) => {
                  next[chatId] = next[chatId].map((m) =>
                    m.id === event.id || m.id === event.wamid
                      ? {
                          ...m,
                          status: event.status,
                          id: event.wamid || m.id,
                          ...(event.mediaType
                            ? {
                                mediaType: event.mediaType,
                                mediaUrl: event.mediaUrl,
                                mediaFileName: event.mediaFileName,
                                mediaStatus: event.mediaStatus,
                                mediaAssetId: event.mediaAssetId,
                              }
                            : {}),
                        }
                      : m,
                  );
                });
                return next;
              });

              if (event.status === "FAILED") {
                const errorMessage =
                  event.metaErrorTitle ||
                  event.metaErrorDetails ||
                  "Unknown error";
                message.error(`Message delivery failed: ${errorMessage}`);
              }
            }
          },
        );

        conversationSub = whatsappWebSocketService.subscribe(
          `/topic/whatsapp/${accountId}/conversations`,
          (body) => {
            const updatedConv = JSON.parse(body);

            // Visibility filter for unknown leads
            if (!updatedConv.clientLeadId && !canViewUnknownLeads) {
              return;
            }

            // If the chat is currently active and has unread messages, automatically mark as read!
            if (
              activeChatIdRef.current === updatedConv.id &&
              updatedConv.unreadCount > 0
            ) {
              postAuthMethod(
                `/api/whatsapp-conversations/${updatedConv.id}/read`,
              ).catch((err) =>
                console.error("Failed to auto-mark as read", err),
              );
              // Optimistically reset it so the UI doesn't flicker
              updatedConv.unreadCount = 0;
            }

            const existingChat = chatsRef.current.find(
              (chat) =>
                chat.conversationId === updatedConv.id ||
                (chat.conversationId === null &&
                  chat.phone === updatedConv.customerPhone),
            );

            if (existingChat) {
              if (
                existingChat.claimedByUserId &&
                existingChat.claimedByUserId === userInfo?.id &&
                updatedConv.claimedByUserId &&
                updatedConv.claimedByUserId !== userInfo?.id
              ) {
                message.warning("Conversation was claimed by Admin");
              }

              setChats((prev) => {
                let foundChat = null;
                let oldTempId = null;
                const filtered = prev.filter((chat) => {
                  if (
                    chat.conversationId === updatedConv.id ||
                    (chat.conversationId === null &&
                      chat.phone === updatedConv.customerPhone)
                  ) {
                    foundChat = chat;
                    if (chat.conversationId === null) {
                      oldTempId = chat.id;
                    }
                    return false;
                  }
                  return true;
                });

                if (oldTempId) {
                  setActiveChatId((prevId) =>
                    prevId === oldTempId ? updatedConv.id : prevId,
                  );
                  setMessagesData((prevMsgs) => {
                    if (prevMsgs[oldTempId]) {
                      const updated = { ...prevMsgs };
                      updated[updatedConv.id] = updated[oldTempId];
                      delete updated[oldTempId];
                      return updated;
                    }
                    return prevMsgs;
                  });
                }

                const lastMsgText =
                  updatedConv.lastMessageMessageBody ||
                  updatedConv.lastMessageBody ||
                  updatedConv.lastMessageText ||
                  updatedConv.lastMessageContent ||
                  updatedConv.lastMessageMessage ||
                  updatedConv.message ||
                  updatedConv.messageBody ||
                  (typeof updatedConv.lastMessage === "string"
                    ? updatedConv.lastMessage
                    : updatedConv.lastMessage?.messageBody ||
                      updatedConv.lastMessage?.text ||
                      updatedConv.lastMessage?.body ||
                      (foundChat ? foundChat.message : ""));
                const isOutbound =
                  updatedConv.lastMessageDirection === "OUTBOUND" ||
                  updatedConv.lastMessage?.direction === "OUTBOUND" ||
                  updatedConv.lastMessageSender === "AGENT" ||
                  updatedConv.lastMessageSender === "SYSTEM" ||
                  updatedConv.lastMessageSender === "OUTBOUND";
                const lastMsgSender = lastMsgText
                  ? isOutbound
                    ? "You"
                    : updatedConv.lastMessageWhatsappProfileName ||
                      updatedConv.lastMessageSenderName ||
                      updatedConv.whatsappProfileName ||
                      updatedConv.customerPhone ||
                      (foundChat ? foundChat.name : "Customer")
                  : "";

                const convTime = updatedConv.lastMessageTime
                  ? new Date(updatedConv.lastMessageTime).getTime()
                  : foundChat
                    ? foundChat.lastMessageTime
                    : 0;

                const updatedChat = {
                  ...foundChat,
                  id: updatedConv.id,
                  conversationId: updatedConv.id,
                  wabaId: updatedConv.wabaId || foundChat.wabaId,
                  whatsappPhoneNumberId: updatedConv.whatsappPhoneNumberId,
                  isPinned: updatedConv.isPinned,
                  isArchived: updatedConv.isArchived,
                  assignedUserId: updatedConv.assignedUserId,
                  claimedByUserId: updatedConv.claimedByUserId,
                  claimedAt: updatedConv.claimedAt,
                  unread: updatedConv.unreadCount,
                  unreadCount: updatedConv.unreadCount,
                  status: updatedConv.status,
                  name: updatedConv.whatsappProfileName || foundChat.name,
                  time: updatedConv.lastMessageTime
                    ? formatWhatsAppChatTime(updatedConv.lastMessageTime)
                    : foundChat.time,
                  lastMessageTime: convTime,
                  message: lastMsgText,
                  lastMessageSender: lastMsgSender,
                  serviceWindowExpiresAt: updatedConv.serviceWindowExpiresAt,
                  email: updatedConv.clientLeadEmail || "N/A",
                  stage: updatedConv.clientLeadStatusName || "No Status",
                  rating: updatedConv.clientLeadRatingText || "No Rating",
                  value: updatedConv.clientLeadOpportunityAmount || "0",
                  clientLeadId:
                    updatedConv.clientLeadId || foundChat.clientLeadId,
                };
                return [updatedChat, ...filtered].sort(
                  (a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0),
                );
              });
            } else {
              // Asynchronous API check for strict backend visibility verification
              getAuthMethod(
                `/api/whatsapp-conversations?id.equals=${updatedConv.id}`,
              )
                .then((res) => {
                  const apiChat = res.data?.[0];
                  if (apiChat) {
                    const existingChat = chatsRef.current.find(
                      (chat) => chat.conversationId === apiChat.id,
                    );
                    if (
                      existingChat &&
                      existingChat.claimedByUserId &&
                      existingChat.claimedByUserId === userInfo?.id &&
                      apiChat.claimedByUserId &&
                      apiChat.claimedByUserId !== userInfo?.id
                    ) {
                      message.warning("Conversation was claimed by Admin");
                    }

                    setChats((prev) => {
                      if (prev.some((c) => c.conversationId === apiChat.id))
                        return prev;

                      if (
                        selectedWabaId &&
                        apiChat.wabaId &&
                        String(apiChat.wabaId) !== String(selectedWabaId)
                      ) {
                        return prev;
                      }

                      if (
                        selectedPhoneId &&
                        apiChat.whatsappPhoneNumberId &&
                        String(apiChat.whatsappPhoneNumberId) !==
                          String(selectedPhoneId)
                      ) {
                        return prev;
                      }

                      const lastMsgText =
                        apiChat.lastMessageMessageBody ||
                        apiChat.lastMessageBody ||
                        apiChat.lastMessageText ||
                        apiChat.lastMessageContent ||
                        apiChat.lastMessageMessage ||
                        apiChat.message ||
                        apiChat.messageBody ||
                        (typeof apiChat.lastMessage === "string"
                          ? apiChat.lastMessage
                          : apiChat.lastMessage?.messageBody ||
                            apiChat.lastMessage?.text ||
                            apiChat.lastMessage?.body ||
                            "");
                      const isOutbound =
                        apiChat.lastMessageDirection === "OUTBOUND" ||
                        apiChat.lastMessage?.direction === "OUTBOUND" ||
                        apiChat.lastMessageSender === "AGENT" ||
                        apiChat.lastMessageSender === "SYSTEM" ||
                        apiChat.lastMessageSender === "OUTBOUND";
                      const lastMsgSender = lastMsgText
                        ? isOutbound
                          ? "You"
                          : apiChat.lastMessageWhatsappProfileName ||
                            apiChat.lastMessageSenderName ||
                            apiChat.whatsappProfileName ||
                            apiChat.customerPhone ||
                            "Customer"
                        : "";

                      const convTime = apiChat.lastMessageTime
                        ? new Date(apiChat.lastMessageTime).getTime()
                        : 0;

                      const newChat = {
                        id: apiChat.id,
                        conversationId: apiChat.id,
                        name:
                          apiChat.whatsappProfileName ||
                          apiChat.clientLeadName ||
                          apiChat.customerPhone,
                        clientLeadName: apiChat.clientLeadName,
                        whatsappProfileName: apiChat.whatsappProfileName,
                        phone: apiChat.customerPhone,
                        wabaId: apiChat.wabaId,
                        whatsappPhoneNumberId: apiChat.whatsappPhoneNumberId,
                        message: lastMsgText,
                        lastMessageSender: lastMsgSender,
                        time: apiChat.lastMessageTime
                          ? formatWhatsAppChatTime(apiChat.lastMessageTime)
                          : "",
                        lastMessageTime: convTime,
                        unread: apiChat.unreadCount || 0,
                        unreadCount: apiChat.unreadCount || 0,
                        status: apiChat.status,
                        serviceWindowExpiresAt: apiChat.serviceWindowExpiresAt,
                        isPinned: apiChat.isPinned || false,
                        isArchived: apiChat.isArchived || false,
                        assignedUserId: apiChat.assignedUserId,
                        claimedByUserId: apiChat.claimedByUserId,
                        claimedAt: apiChat.claimedAt,
                        avatar: apiChat.avatarUrl || apiChat.avatar || null,
                        email: apiChat.clientLeadEmail || "N/A",
                        stage: apiChat.clientLeadStatusName || "No Status",
                        rating: apiChat.clientLeadRatingText || "No Rating",
                        value: apiChat.clientLeadOpportunityAmount || "0",
                        clientLeadId: apiChat.clientLeadId,
                      };
                      return [newChat, ...prev].sort(
                        (a, b) =>
                          (b.lastMessageTime || 0) - (a.lastMessageTime || 0),
                      );
                    });
                  }
                })
                .catch((err) =>
                  console.error("Visibility verification failed", err),
                );
            }
          },
        );

        conversationDeletedSub = whatsappWebSocketService.subscribe(
          `/topic/whatsapp/${accountId}/conversations/deleted`,
          (body) => {
            const event = JSON.parse(body);
            const deletedConversationId = event.conversationId;

            setChats((prev) =>
              prev.filter((c) => c.conversationId !== deletedConversationId),
            );

            setMessagesData((prev) => {
              const updated = { ...prev };
              delete updated[deletedConversationId];
              return updated;
            });

            if (activeChatIdRef.current === deletedConversationId) {
              setActiveChatId(null);
            }
          },
        );
      })
      .catch((err) => console.error("Failed to connect WhatsApp STOMP:", err));

    return () => {
      if (messageSub) messageSub.unsubscribe();
      if (statusSub) statusSub.unsubscribe();
      if (conversationSub) conversationSub.unsubscribe();
      if (conversationDeletedSub) conversationDeletedSub.unsubscribe();
      whatsappWebSocketService.disconnect();
    };
  }, [accountId, selectedPhoneId]);

  const [templates, setTemplates] = useState([]);
  const [syncingTemplates, setSyncingTemplates] = useState(false);
  const [verifyingSubscription, setVerifyingSubscription] = useState(false);

  const handleVerifySubscription = () => {
    setVerifyingSubscription(true);
    postAuthMethod("/api/whatsapp/accounts/subscription/verify")
      .then(() => {
        message.success(
          "Webhook subscription verification triggered successfully!",
        );

        // Poll for updated status since the verification happens asynchronously
        let pollCount = 0;
        const pollInterval = setInterval(() => {
          getAuthMethod("/api/whatsapp/accounts/status")
            .then((res) => {
              if (res.data) {
                setLocalConnectionData(res.data);
                if (
                  res.data.subscriptionVerified === true ||
                  res.data.subscriptionError ||
                  pollCount > 3
                ) {
                  clearInterval(pollInterval);
                  setVerifyingSubscription(false);
                }
              }
            })
            .catch(() => {
              clearInterval(pollInterval);
              setVerifyingSubscription(false);
            });

          pollCount++;
          if (pollCount > 4) {
            clearInterval(pollInterval);
            setVerifyingSubscription(false);
          }
        }, 2000);
      })
      .catch((err) => {
        console.error("Subscription verification error", err);
        message.error(
          "Failed to verify subscription: " +
            (err.response?.data?.title ||
              err.response?.data?.message ||
              err.message),
        );
        setVerifyingSubscription(false);
      });
  };

  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    language: "en_US",
    category: "UTILITY",
    bodyText: "",
  });

  const handleCreateTemplate = () => {
    if (!newTemplate.name || !newTemplate.bodyText) {
      message.error("Name and Body are required.");
      return;
    }
    setCreatingTemplate(true);
    postAuthMethod("/api/whatsapp/templates", newTemplate)
      .then((res) => {
        message.success("Template created and synced successfully!");
        setTemplates(res.data || []);
        setIsCreateModalVisible(false);
        setNewTemplate({
          name: "",
          language: "en_US",
          category: "UTILITY",
          bodyText: "",
        });
      })
      .catch((err) => {
        console.error("Create template error", err);
        message.error(
          "Failed to create template: " +
            (err.response?.data?.title ||
              err.response?.data?.message ||
              err.message),
        );
      })
      .finally(() => setCreatingTemplate(false));
  };

  const sortLatestTemplates = (list) => {
    if (!Array.isArray(list)) return [];
    return [...list].sort((a, b) => {
      const dateA = new Date(
        a.createdDate || a.createdAt || a.updatedAt || a.lastModifiedDate || 0,
      ).getTime();
      const dateB = new Date(
        b.createdDate || b.createdAt || b.updatedAt || b.lastModifiedDate || 0,
      ).getTime();
      if (dateA && dateB && dateA !== dateB) return dateB - dateA;
      const idA = typeof a.id === "number" ? a.id : parseInt(a.id, 10);
      const idB = typeof b.id === "number" ? b.id : parseInt(b.id, 10);
      if (!isNaN(idA) && !isNaN(idB)) return idB - idA;
      return 0;
    });
  };

  const fetchTemplates = () => {
    getAuthMethod("/api/whatsapp/templates")
      .then((res) => {
        const sorted = sortLatestTemplates(res.data || []);
        setTemplates(sorted);
        if (sorted.length > 0 && !selectedTemplate) {
          const approved = sorted.find((t) => t.status === "APPROVED");
          if (approved) setSelectedTemplate(approved);
        }
      })
      .catch((err) => console.error("Failed to fetch templates", err));
  };

  const handleSyncTemplates = () => {
    setSyncingTemplates(true);
    postAuthMethod("/api/whatsapp/templates/sync")
      .then((res) => {
        message.success("Templates synced successfully!");
        setTemplates(sortLatestTemplates(res.data || []));
      })
      .catch((err) => {
        console.error("Sync templates error", err);
        message.error("Failed to sync templates.");
      })
      .finally(() => setSyncingTemplates(false));
  };

  const handleDeleteMessage = (messageId) => {
    deleteAuthMethod(`/api/whatsapp/messages/${messageId}`)
      .then(() => {
        message.success("Message deleted");
        setMessagesData((prev) => ({
          ...prev,
          [activeChatId]: (prev[activeChatId] || []).filter(
            (msg) => msg.id !== messageId,
          ),
        }));
      })
      .catch((err) => {
        console.error("Failed to delete message", err);
        message.error("Failed to delete message");
      });
  };

  const handleTestConnection = () => {
    if (!testPhone.trim()) {
      message.error("Please enter a phone number.");
      return;
    }
    if (messageType === "TEMPLATE" && !selectedTemplate) {
      message.error("Please select a template.");
      return;
    }

    setTestLoading(true);
    setWamid("");

    postAuthMethod("/api/whatsapp/messages/test", {
      phoneNumber: testPhone,
      body: testBody,
      messageType: messageType,
      templateName: selectedTemplate?.name,
      templateLanguage: selectedTemplate?.language,
    })
      .then((response) => {
        message.success("Test message sent successfully!");
        if (response.data && response.data.wamid) {
          setWamid(response.data.wamid);
        }
      })
      .catch((error) => {
        console.error("Test message error", error);
        message.error(
          "Failed to send test message: " +
            (error.response?.data?.message || error.message),
        );
      })
      .finally(() => {
        setTestLoading(false);
      });
  };

  const activeChat =
    selectedAccountChats.find((c) => c.id === activeChatId) || null;
  const activeMessages = messagesData[activeChatId] || [];

  const handleSelectChat = (chatId) => {
    setActiveChatId(chatId);
    setMobileView("content");
    setMobilePanel("chat");

    const chat = chats.find((c) => c.id === chatId);
    if (chat && chat.conversationId && chat.unreadCount > 0) {
      postAuthMethod(`/api/whatsapp-conversations/${chat.conversationId}/read`)
        .then(() => {
          // Optimistically update local state
          setChats((prev) =>
            prev.map((c) =>
              c.conversationId === chat.conversationId
                ? { ...c, unreadCount: 0, unread: 0 }
                : c,
            ),
          );
        })
        .catch((err) => console.error("Failed to mark chat as read", err));
    }
  };

  const handleStartNewChat = () => {
    if (!selectedLeadId) {
      message.error("Please select a lead first.");
      return;
    }

    const lead =
      selectedLead || leadSearchResults.find((l) => l.id === selectedLeadId);
    const phone = lead?.phoneNo || lead?.mobile;
    if (!lead || !phone) {
      message.error("Selected lead does not have a valid phone number.");
      return;
    }

    // Switch to the correct inbox context if a specific number was selected
    const phoneToUse =
      newChatPhoneId ||
      selectedPhoneId ||
      localConnectionData?.phoneNumbers?.[0]?.id;
    if (phoneToUse && phoneToUse !== selectedPhoneId) {
      handleSelectPhone(phoneToUse);
    }

    // Give state time to update before injecting chat
    setTimeout(() => {
      // Check if chat already exists
      const existingChat = chats.find(
        (c) =>
          c.phone === phone || c.id === phone || c.clientLeadId === lead.id,
      );

      if (existingChat) {
        handleSelectChat(existingChat.id);
      } else {
        // Inject new chat into list
        const newChat = {
          id: phone,
          conversationId: null, // it will get created on first message
          name: lead.firstname || lead.fullname || phone,
          clientLeadName:
            `${lead.firstname || ""} ${lead.lastname || ""}`.trim() ||
            lead.fullname,
          clientLeadId: lead.id,
          whatsappProfileName: lead.firstname || lead.fullname,
          phone: phone,
          message: "",
          time: formatWhatsAppChatTime(new Date()),
          lastMessageTime: new Date().getTime(),
          unread: 0,
          status: "New",
          avatar: null,
          whatsappPhoneNumberId: phoneToUse,
          email: lead.emailId || "N/A",
          stage: "New Lead", // Optimistic default
          rating: lead.leadRating || "No Rating",
          value: lead.oppurtunityAmount || "0",
        };
        setChats((prevChats) =>
          [newChat, ...prevChats].sort(
            (a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0),
          ),
        );
        handleSelectChat(newChat.id);
      }

      setIsNewChatModalVisible(false);
      setLeadSearchQuery("");
      setSelectedLeadId(null);
      setSelectedLead(null);
      setNewChatPhoneId(null);
    }, 100);
  };

  const handleSendMessage = (text, mediaId = null) => {
    if (
      (!text || !text.trim()) &&
      (!mediaId || (Array.isArray(mediaId) && mediaId.length === 0))
    )
      return;
    if (!activeChatId) return;

    const activeChat = chats.find((c) => c.id === activeChatId);
    if (!activeChat) return;

    const mediaIds = Array.isArray(mediaId)
      ? mediaId.filter(Boolean)
      : mediaId
        ? [mediaId]
        : [];

    if (mediaIds.length === 0) {
      postAuthMethod("/api/whatsapp/messages", {
        recipientPhone: activeChat.phone,
        body: text,
        messageType: "TEXT",
        whatsappPhoneNumberId:
          activeChat.whatsappPhoneNumberId || selectedPhoneId || undefined,
      })
        .then((res) => {
          fetchMessages(activeChatId);
          fetchChats(0);
        })
        .catch((err) => {
          console.error("Failed to send message", err);
          const errorDetail =
            err.response?.data?.detail ||
            err.response?.data?.title ||
            err.response?.data?.message ||
            err.message;
          message.error("Failed to send message: " + errorDetail);
          if (activeChat.conversationId === null) {
            setChats((prev) => prev.filter((c) => c.id !== activeChat.id));
            setActiveChatId(null);
            setMobileView("list");
          }
        });
    } else {
      const promises = mediaIds.map((mId, idx) =>
        postAuthMethod("/api/whatsapp/messages", {
          recipientPhone: activeChat.phone,
          body: idx === 0 ? text || undefined : undefined,
          messageType: "IMAGE",
          mediaAssetId: mId,
          whatsappPhoneNumberId:
            activeChat.whatsappPhoneNumberId || selectedPhoneId || undefined,
        }),
      );

      Promise.all(promises)
        .then(() => {
          fetchMessages(activeChatId);
          fetchChats(0);
        })
        .catch((err) => {
          console.error("Failed to send media messages", err);
          const errorDetail =
            err.response?.data?.detail ||
            err.response?.data?.title ||
            err.response?.data?.message ||
            err.message;
          message.error("Failed to send media: " + errorDetail);
        });
    }
  };

  const handleSendTemplate = (template, mappingConfig) => {
    if (!template || !activeChatId) return;

    const activeChat = chats.find((c) => c.id === activeChatId);
    if (!activeChat) return;

    let resolvedBody = template.bodyText;
    if (!resolvedBody && template.components) {
      try {
        const comps =
          typeof template.components === "string"
            ? JSON.parse(template.components)
            : template.components;
        const bodyComp = comps.find((c) => c.type === "BODY");
        if (bodyComp && bodyComp.text) resolvedBody = bodyComp.text;
      } catch (e) {}
    }

    return postAuthMethod("/api/whatsapp/messages", {
      recipientPhone: activeChat.phone,
      body: resolvedBody || `[${template.name}]`,
      messageType: "TEMPLATE",
      templateName: template.name,
      templateLanguage: template.language,
      mappingConfig: mappingConfig,
      whatsappPhoneNumberId:
        activeChat.whatsappPhoneNumberId || selectedPhoneId || undefined,
    })
      .then((res) => {
        message.success("Template sent successfully!");
        fetchMessages(activeChatId);
        fetchChats();
      })
      .catch((err) => {
        console.error("Failed to send template", err);
        const errorDetail =
          err.response?.data?.detail ||
          err.response?.data?.title ||
          err.response?.data?.message ||
          err.message;
        // message.error handled by caller
        if (activeChat.conversationId === null) {
          setChats((prev) => prev.filter((c) => c.id !== activeChat.id));
          setActiveChatId(null);
          setMobileView("list");
        }
        throw new Error(errorDetail);
      });
  };

  const handleDisconnect = () => {
    Modal.confirm({
      title: "Disconnect WhatsApp?",
      content:
        "Are you sure you want to disconnect your WhatsApp Business Account? You can reconnect it later without losing historical chats.",
      okText: "Disconnect",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        return deleteAuthMethod("/api/whatsapp/accounts")
          .then(() => {
            message.success("WhatsApp disconnected successfully.");
            if (onDisconnect) onDisconnect();
          })
          .catch((error) => {
            console.error("Disconnect error", error);
            message.error("Failed to disconnect WhatsApp.");
          });
      },
    });
  };

  const handleDeleteChat = () => {
    if (!activeChatId) return;
    const activeChat = chats.find((c) => c.id === activeChatId);
    if (!activeChat || !activeChat.conversationId) {
      message.error("Cannot delete chat without an active conversation.");
      return;
    }

    Modal.confirm({
      title: "Delete Chat?",
      content:
        "Are you sure you want to permanently delete this chat and its entire history? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        return deleteAuthMethod(
          `/api/whatsapp-conversations/${activeChat.conversationId}`,
        )
          .then(() => {
            message.success("Chat deleted successfully.");
            setActiveChatId(null);
            fetchChats();
          })
          .catch((error) => {
            console.error("Delete chat error", error);
            message.error("Failed to delete chat.");
          });
      },
    });
  };

  const handleTogglePin = (conversationId, isPinned) => {
    patchAuthMethod(`/api/whatsapp-conversations`, `${conversationId}/pin`, {
      isPinned,
    }).catch((err) => message.error("Failed to pin chat"));
  };

  const handleToggleArchive = (conversationId, isArchived) => {
    patchAuthMethod(
      `/api/whatsapp-conversations`,
      `${conversationId}/archive`,
      { isArchived },
    ).catch((err) => message.error("Failed to archive chat"));
  };

  const handleAssignChat = (conversationId, userId) => {
    patchAuthMethod(`/api/whatsapp-conversations`, `${conversationId}/assign`, {
      assignedUserId: userId,
    }).catch((err) => message.error("Failed to assign chat"));
  };

  const activePhone =
    localConnectionData?.phoneNumbers?.find((p) => p.id === selectedPhoneId) ||
    localConnectionData?.phoneNumbers?.[0];
  const quota = activePhone?.quotaSnapshot || {};
  const percentUsed =
    quota.tierLimit > 0 ? (quota.estimatedOpened / quota.tierLimit) * 100 : 0;
  const isApproachingLimit = percentUsed >= 85;

  return (
    <div className="tw-p-3 md:tw-p-4 tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
      {isApproachingLimit && (
        <div className="tw-bg-[#fffbeb] tw-border tw-border-solid tw-border-[#fcd34d] tw-rounded-lg tw-p-3 tw-mb-4 tw-flex tw-items-center tw-gap-3 tw-shadow-sm">
          <WarningOutlined className="tw-text-[#d97706] tw-text-lg" />
          <div className="tw-text-sm tw-text-[#92400e]">
            <strong>⚠ Messaging capacity is almost exhausted.</strong> New
            conversations may not open. Existing conversations continue to work
            normally.
          </div>
        </div>
      )}

      <div className="tw-flex-1 tw-flex tw-flex-col xl:tw-flex-row tw-gap-3 tw-overflow-hidden tw-pb-4 tw-min-h-0">
        <div
          className={`tw-w-full xl:tw-w-auto ${
            mobileView === "content" ? "tw-hidden xl:tw-flex" : "tw-flex"
          }`}
        >
          <ChatListSidebar
            chats={chats}
            activeChatId={activeChatId}
            onSelectChat={handleSelectChat}
            onNewChat={() => setIsNewChatModalVisible(true)}
            connectionData={localConnectionData || connectionData}
            selectedPhoneId={selectedPhoneId}
            selectedWabaId={selectedWabaId}
            onSelectPhone={handleSelectPhone}
            loadMoreChats={loadMoreChats}
            hasMoreChats={hasMoreChats}
            onSearchChange={setGlobalSearchQuery}
          />
        </div>
        <div
          className={`tw-flex tw-flex-1 tw-rounded-xl tw-overflow-hidden tw-bg-white tw-border tw-border-solid tw-border-border-secondary tw-min-h-0 ${
            mobileView === "content" ? "tw-flex" : "tw-hidden xl:tw-flex"
          }`}
        >
          <div className="tw-flex-1 tw-flex tw-flex-col tw-min-h-0 tw-min-w-0">
            <div className="tw-px-4 md:tw-px-6 tw-py-3 tw-bg-white tw-border-b tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-border-secondary tw-flex tw-items-center tw-justify-between tw-gap-3 tw-shrink-0">
              <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0 tw-flex-1">
                {activeChat ? (
                  <>
                    <div className="tw-flex tw-items-center tw-gap-2.5 tw-min-w-0">
                      <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => setMobileView("list")}
                        className="xl:tw-hidden !tw-px-0 tw-text-sub-text"
                      />
                      <UserInitialsAvatar
                        name={activeChat.name}
                        phone={activeChat.phone}
                        src={activeChat.avatar}
                        size={38}
                        className="tw-shrink-0 tw-shadow-sm"
                      />
                      <div className="tw-min-w-0">
                        <div className="tw-flex tw-items-center tw-gap-2">
                          <h3 className="tw-text-sm tw-font-bold tw-text-text-primary tw-m-0 tw-whitespace-nowrap">
                            {activeChat.name || activeChat.phone}
                          </h3>
                          {activeChat.status && (
                            <div className="tw-flex tw-items-center tw-gap-1 tw-bg-[#E8F7EE] tw-px-2 tw-py-0.5 tw-rounded-full tw-shrink-0">
                              <span className="tw-w-1.5 tw-h-1.5 tw-bg-[#25D366] tw-rounded-full"></span>
                              <span className="tw-text-[9px] tw-text-[#25D366] tw-font-bold tw-uppercase">
                                {activeChat.status}
                              </span>
                            </div>
                          )}
                        </div>
                        {activeChat.name &&
                          activeChat.name !== activeChat.phone && (
                            <span className="tw-text-[11px] tw-font-medium tw-text-sub-text tw-block tw-mt-0.5 tw-whitespace-nowrap">
                              {activeChat.phone}
                            </span>
                          )}
                      </div>

                      {activeChatId && (
                        <div className="tw-flex tw-items-center tw-gap-2 tw-ml-3 tw-shrink-0">
                          {!activeChat?.clientLeadId && (
                            <Tooltip title="Click to create lead">
                              <span
                                onClick={() => setShowAddLeadModal(true)}
                                className="tw-cursor-pointer hover:tw-bg-rose-100 tw-transition-colors tw-whitespace-nowrap tw-text-[10px] tw-font-semibold tw-text-rose-600 tw-bg-rose-50 tw-border tw-border-solid tw-border-rose-200 tw-px-2.5 tw-py-1 tw-rounded-full tw-inline-flex tw-items-center tw-gap-1"
                              >
                                <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-rose-500"></span>
                                Unknown Lead
                              </span>
                            </Tooltip>
                          )}

                          {activeChat?.isPinned && (
                            <Tooltip title="Pinned Conversation">
                              <PushpinFilled className="tw-text-amber-500 tw-text-xs" />
                            </Tooltip>
                          )}

                          {/* More Actions Dropdown Menu */}
                          <Dropdown
                            menu={{
                              items: [
                                ...(!activeChat?.clientLeadId
                                  ? [
                                      {
                                        key: "create-lead",
                                        icon: (
                                          <UserAddOutlined className="tw-text-[#075E54]" />
                                        ),
                                        label: "Create Lead",
                                        onClick: () =>
                                          setShowAddLeadModal(true),
                                      },
                                    ]
                                  : []),
                                {
                                  key: "copy-phone",
                                  icon: <CopyOutlined />,
                                  label: "Copy Phone Number",
                                  onClick: () => {
                                    if (activeChat?.phone) {
                                      navigator.clipboard.writeText(
                                        activeChat.phone,
                                      );
                                      message.success(
                                        "Phone number copied to clipboard!",
                                      );
                                    }
                                  },
                                },
                                {
                                  key: "pin",
                                  icon: activeChat?.isPinned ? (
                                    <PushpinFilled className="tw-text-amber-500" />
                                  ) : (
                                    <PushpinOutlined />
                                  ),
                                  label: activeChat?.isPinned
                                    ? "Unpin Chat"
                                    : "Pin Chat",
                                  onClick: () =>
                                    handleTogglePin(
                                      activeChat.conversationId,
                                      !activeChat.isPinned,
                                    ),
                                },
                                {
                                  key: "archive",
                                  icon: (
                                    <InboxOutlined
                                      className={
                                        activeChat?.isArchived
                                          ? "tw-text-indigo-600"
                                          : ""
                                      }
                                    />
                                  ),
                                  label: activeChat?.isArchived
                                    ? "Unarchive Chat"
                                    : "Archive Chat",
                                  onClick: () =>
                                    handleToggleArchive(
                                      activeChat.conversationId,
                                      !activeChat.isArchived,
                                    ),
                                },
                                {
                                  type: "divider",
                                },
                                {
                                  key: "delete",
                                  icon: <DeleteOutlined />,
                                  danger: true,
                                  label: "Delete Chat",
                                  onClick: handleDeleteChat,
                                },
                              ],
                            }}
                            trigger={["click"]}
                            placement="bottomRight"
                          >
                            <Tooltip title="More Actions">
                              <button
                                type="button"
                                className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-[#F8FAFC] tw-text-[#475569] tw-border tw-border-solid tw-border-[#E2E8F0] hover:tw-bg-white hover:tw-border-[#CBD5E1] hover:tw-text-[#075E54] tw-transition-all tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-outline-none"
                              >
                                <EllipsisOutlined className="tw-text-sm" />
                              </button>
                            </Tooltip>
                          </Dropdown>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <h3 className="tw-text-base tw-font-bold tw-text-text-primary tw-m-0">
                      WhatsApp Inbox
                    </h3>
                  </div>
                )}
              </div>

              <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
                {["local", "development"].includes(
                  process.env.REACT_APP_ENV,
                ) && (
                  <Tooltip title="Test Connection">
                    <button
                      type="button"
                      onClick={() => setIsTestModalVisible(true)}
                      className="tw-w-9 tw-h-9 tw-rounded-xl tw-bg-white hover:tw-bg-amber-50 tw-text-slate-500 hover:tw-text-[#D48806] tw-border tw-border-solid tw-border-slate-200 hover:tw-border-[#FFE58F] tw-transition-all tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-outline-none"
                    >
                      <BugOutlined className="tw-text-xs" />
                    </button>
                  </Tooltip>
                )}
                <Tooltip title="Disconnect Account">
                  <button
                    type="button"
                    onClick={handleDisconnect}
                    className="tw-w-9 tw-h-9 tw-rounded-xl tw-bg-white hover:tw-bg-rose-50 tw-text-slate-500 hover:tw-text-[#F5222D] tw-border tw-border-solid tw-border-slate-200 hover:tw-border-[#FFCCC7] tw-transition-all tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-outline-none"
                  >
                    <DisconnectOutlined className="tw-text-xs" />
                  </button>
                </Tooltip>
                <Button
                  type="primary"
                  onClick={() => {
                    if (onOpenCampaigns) {
                      onOpenCampaigns();
                    } else {
                      navigate("/whatsapp-campaigns");
                    }
                  }}
                  className="!tw-bg-[#25D366] hover:!tw-bg-[#25D366] !tw-border-[#25D366] hover:!tw-border-[#25D366] !tw-text-white hover:!tw-text-white !tw-text-xs md:!tw-text-sm !tw-font-bold tw-flex tw-items-center tw-gap-1.5 !tw-h-8 !tw-px-4 !tw-rounded-full tw-shadow-none tw-whitespace-nowrap"
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor"></path>
                    <path
                      d="M15.54 8.46a5 5 0 0 1 0 7.07"
                      strokeLinecap="round"
                    ></path>
                    <path
                      d="M19.07 4.93a10 10 0 0 1 0 14.14"
                      strokeLinecap="round"
                    ></path>
                  </svg>
                  Campaign
                </Button>
              </div>
            </div>

            {activeChat ? (
              <div className="tw-flex-1 tw-flex tw-flex-col lg:tw-flex-row tw-min-h-0 tw-min-w-0">
                <div
                  className={`tw-flex-1 tw-min-h-0 tw-min-w-0 ${
                    mobilePanel === "details"
                      ? "tw-hidden lg:tw-flex"
                      : "tw-flex"
                  }`}
                >
                  <ChatWindow
                    activeChat={activeChat}
                    activeMessages={activeMessages}
                    onSendMessage={handleSendMessage}
                    templates={templates}
                    onSendTemplate={handleSendTemplate}
                    onCreateTemplate={() => setIsCreateModalVisible(true)}
                    loadMoreMessages={() => {
                      if (activeChatId && hasMoreMessages[activeChatId]) {
                        fetchMessages(
                          activeChatId,
                          (messagePages[activeChatId] || 0) + 1,
                        );
                      }
                    }}
                    hasMoreMessages={hasMoreMessages[activeChatId]}
                    onTogglePin={() =>
                      handleTogglePin(
                        activeChat.conversationId,
                        !activeChat.isPinned,
                      )
                    }
                    onToggleArchive={() =>
                      handleToggleArchive(
                        activeChat.conversationId,
                        !activeChat.isArchived,
                      )
                    }
                    onAssignChat={(userId) =>
                      handleAssignChat(activeChat.conversationId, userId)
                    }
                    onDeleteMessage={handleDeleteMessage}
                    error={messageErrors[activeChatId]}
                    onClaim={() =>
                      handleClaimConversation(activeChat.conversationId)
                    }
                    onReleaseClaim={() =>
                      handleReleaseClaim(activeChat.conversationId)
                    }
                    onOverrideClaim={() =>
                      handleOverrideClaim(activeChat.conversationId)
                    }
                    currentUser={userInfo}
                    canOverride={isAdmin || hasViewTeam}
                  />
                </div>

                {/* Right Sidebar - Lead Detail */}
                {!isLeadDetailCollapsed && (
                  <div
                    className={`tw-w-full lg:tw-w-auto lg:tw-flex ${
                      mobilePanel === "details"
                        ? "tw-flex"
                        : "tw-hidden lg:tw-flex"
                    }`}
                  >
                    <LeadDetailSidebar
                      activeChat={activeChat}
                      onAssignChat={(userId) =>
                        handleAssignChat(activeChat.conversationId, userId)
                      }
                      onStatusChange={(conversationId, newStatusText) => {
                        setChats((prevChats) =>
                          prevChats.map((chat) =>
                            chat.conversationId === conversationId
                              ? { ...chat, stage: newStatusText }
                              : chat,
                          ),
                        );
                      }}
                      onClose={toggleLeadDetail}
                    />
                  </div>
                )}

                {/* Collapsed Dock Strip on Desktop */}
                {isLeadDetailCollapsed && (
                  <div
                    className="tw-hidden lg:tw-flex tw-flex-col tw-items-center tw-justify-start tw-border-l tw-border-t-0 tw-border-b-0 tw-border-r-0 tw-border-solid tw-border-slate-200 tw-bg-slate-50/70 hover:tw-bg-slate-100/80 tw-transition-colors tw-py-4 tw-px-1.5 tw-shrink-0 tw-cursor-pointer"
                    onClick={toggleLeadDetail}
                  >
                    <Tooltip title="Expand Lead Details" placement="left">
                      <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                        <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-white tw-shadow-xs tw-border tw-border-solid tw-border-slate-200 tw-flex tw-items-center tw-justify-center tw-text-slate-600 hover:tw-text-[#075E54] tw-transition-colors">
                          <PanelRightOpen size={16} />
                        </div>
                        <span
                          className="tw-text-[10px] tw-font-bold tw-tracking-widest tw-text-slate-400 hover:tw-text-[#075E54] tw-select-none"
                          style={{
                            writingMode: "vertical-rl",
                            transform: "rotate(180deg)",
                          }}
                        >
                          LEAD DETAILS
                        </span>
                      </div>
                    </Tooltip>
                  </div>
                )}
              </div>
            ) : (
              <div className="tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-[#f8fafc] tw-p-8 tw-text-center tw-relative tw-overflow-hidden">
                <div className="tw-absolute tw-w-96 tw-h-96 tw-bg-[#25D366]/5 tw-rounded-full tw-blur-3xl tw-pointer-events-none -tw-top-12 -tw-right-12" />
                <div className="tw-absolute tw-w-80 tw-h-80 tw-bg-[#5D44FF]/5 tw-rounded-full tw-blur-3xl tw-pointer-events-none -tw-bottom-12 -tw-left-12" />

                <div className="tw-max-w-md tw-flex tw-flex-col tw-items-center tw-z-10">
                  <div className="tw-w-20 tw-h-20 tw-rounded-3xl tw-bg-white tw-border tw-border-solid tw-border-slate-200/90 tw-shadow-sm tw-flex tw-items-center tw-justify-center tw-mb-6 tw-relative">
                    <WhatsAppOutlined className="tw-text-4xl tw-text-[#25D366]" />
                    <span className="tw-absolute -tw-bottom-1 -tw-right-1 tw-w-4 tw-h-4 tw-bg-emerald-500 tw-rounded-full tw-border-2 tw-border-solid tw-border-white" />
                  </div>

                  <h2 className="tw-text-xl tw-font-bold tw-text-slate-800 tw-mb-2 tw-font-inter">
                    WhatsApp Business Inbox
                  </h2>
                  <p className="tw-text-xs sm:tw-text-sm tw-text-slate-500 tw-leading-relaxed tw-mb-6 tw-m-0">
                    Send and receive real-time WhatsApp messages with your
                    leads. Select a conversation from the left sidebar or click
                    below to start a new chat.
                  </p>

                  <Button
                    type="primary"
                    onClick={() => setIsNewChatModalVisible(true)}
                    className="!tw-h-11 !tw-px-6 !tw-bg-[#25D366] hover:!tw-bg-[#25D366] !tw-border-[#25D366] hover:!tw-border-[#25D366] !tw-text-white hover:!tw-text-white !tw-rounded-xl !tw-font-bold tw-inline-flex tw-items-center tw-gap-2 tw-mb-8 !tw-shadow-none"
                  >
                    <PlusOutlined />
                    Start New Chat
                  </Button>

                  <div className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-full tw-bg-slate-100/80 tw-border tw-border-solid tw-border-slate-200/70 tw-text-slate-500 tw-text-xs tw-font-medium">
                    <LockOutlined className="tw-text-slate-400 tw-text-xs" />
                    <span>
                      End-to-End Encrypted & Official Meta API Verified
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        title="Test WhatsApp Connection"
        visible={isTestModalVisible}
        onCancel={() => setIsTestModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <div className="tw-mt-4">
          <p className="tw-text-sub-text tw-mb-4">
            Send a test message to a WhatsApp number to verify your connection
            and webhooks.
          </p>

          <div className="tw-mb-6 tw-p-4 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm">
            <div className="tw-flex tw-justify-between tw-items-start">
              <div>
                <h4 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-m-0 tw-mb-1">
                  Webhook Subscription
                </h4>
                <div className="tw-text-xs tw-text-gray-500 tw-flex tw-flex-col tw-gap-1">
                  <span>
                    Status:{" "}
                    {localConnectionData?.subscriptionVerified === true ? (
                      <Tag color="success">Verified</Tag>
                    ) : localConnectionData?.subscriptionVerified === false ? (
                      <Tag color="error">Unverified / Failed</Tag>
                    ) : (
                      <Tag color="default">Unknown</Tag>
                    )}
                  </span>
                  {localConnectionData?.lastSubscriptionCheck && (
                    <span>
                      Last Checked:{" "}
                      {new Date(
                        localConnectionData.lastSubscriptionCheck,
                      ).toLocaleString()}
                    </span>
                  )}
                  {localConnectionData?.subscriptionError && (
                    <span className="tw-text-red-500">
                      Error: {localConnectionData.subscriptionError}
                    </span>
                  )}
                </div>
              </div>
              <Button
                size="small"
                type="primary"
                ghost
                loading={verifyingSubscription}
                onClick={handleVerifySubscription}
              >
                Verify Subscription
              </Button>
            </div>
          </div>

          <Form layout="vertical" onFinish={handleTestConnection}>
            <Form.Item label="Message Type">
              <Radio.Group
                value={messageType}
                onChange={(e) => setMessageType(e.target.value)}
                buttonStyle="solid"
                className="tw-w-full tw-flex"
              >
                <Radio.Button
                  value="TEMPLATE"
                  className="tw-flex-1 tw-text-center"
                >
                  Template
                </Radio.Button>
                <Radio.Button value="TEXT" className="tw-flex-1 tw-text-center">
                  Custom Text
                </Radio.Button>
              </Radio.Group>
              <div className="tw-mt-2 tw-text-xs tw-text-gray-500">
                {messageType === "TEMPLATE"
                  ? "Template messages will ALWAYS be delivered, regardless of the 24-hour customer service window."
                  : "WARNING: Custom text messages will FAIL to deliver unless the recipient has messaged you within the last 24 hours."}
              </div>
            </Form.Item>

            {messageType === "TEMPLATE" && (
              <div className="tw-flex tw-flex-col tw-gap-3 tw-mb-4">
                <div className="tw-flex tw-justify-between tw-items-center">
                  <span className="tw-text-sm tw-font-medium">
                    Select Approved Template
                  </span>
                  <div className="tw-flex tw-gap-2">
                    <Button
                      type="link"
                      icon={<SyncOutlined spin={syncingTemplates} />}
                      onClick={handleSyncTemplates}
                      loading={syncingTemplates}
                      className="!tw-px-0"
                    >
                      Sync
                    </Button>
                    <Button
                      type="link"
                      icon={<PlusOutlined />}
                      onClick={() => setIsCreateModalVisible(true)}
                      className="!tw-px-0 tw-text-[#075E54]"
                    >
                      Create New
                    </Button>
                  </div>
                </div>
                <Select
                  value={selectedTemplate?.id}
                  onChange={(val) => {
                    const t = templates.find((temp) => temp.id === val);
                    setSelectedTemplate(t);
                  }}
                  placeholder="Select a template"
                  className="tw-w-full"
                  optionLabelProp="label"
                  notFoundContent="No templates found. Click sync."
                >
                  {templates
                    .filter((t) => t.status === "APPROVED")
                    .map((t) => (
                      <Option
                        key={t.id}
                        value={t.id}
                        label={`${t.name} (${t.language})`}
                      >
                        <div className="tw-flex tw-justify-between tw-items-center">
                          <span>
                            {t.name}{" "}
                            <span className="tw-text-gray-400 tw-text-xs">
                              ({t.language})
                            </span>
                          </span>
                          <Tag
                            color="success"
                            className="tw-mr-0 tw-text-[10px]"
                          >
                            APPROVED
                          </Tag>
                        </div>
                      </Option>
                    ))}
                </Select>
                {selectedTemplate && (
                  <div className="tw-bg-gray-50 tw-p-2 tw-rounded tw-text-xs tw-text-gray-600 tw-border tw-border-solid tw-border-gray-200">
                    Will send <strong>{selectedTemplate.name}</strong> with
                    language code <strong>{selectedTemplate.language}</strong>.
                  </div>
                )}
              </div>
            )}

            <Form.Item
              label="Destination Phone Number (e.g. +919876543210)"
              required
            >
              <Input
                placeholder="+919876543210"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
              />
            </Form.Item>

            {messageType === "TEXT" && (
              <Form.Item label="Message Body" required>
                <Input.TextArea
                  placeholder="Enter your test message here..."
                  value={testBody}
                  onChange={(e) => setTestBody(e.target.value)}
                  rows={4}
                />
              </Form.Item>
            )}

            {wamid && (
              <div className="tw-mb-4 tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border tw-border-gray-200">
                <span className="tw-text-xs tw-text-gray-500 tw-block">
                  Message ID (wamid)
                </span>
                <span className="tw-text-sm tw-font-mono tw-text-gray-800 tw-break-all">
                  {wamid}
                </span>

                {messageStatus && (
                  <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-b-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-gray-200">
                    <span className="tw-text-xs tw-text-gray-500 tw-block tw-mb-1">
                      Delivery Status
                    </span>
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <Tag
                        color={
                          messageStatus.status === "ACCEPTED"
                            ? "default"
                            : messageStatus.status === "SENT"
                              ? "processing"
                              : messageStatus.status === "DELIVERED"
                                ? "success"
                                : messageStatus.status === "READ"
                                  ? "purple"
                                  : "error"
                        }
                      >
                        {messageStatus.status}
                      </Tag>
                      {messageStatus.statusTimestamp && (
                        <span className="tw-text-xs tw-text-gray-400">
                          {new Date(
                            messageStatus.statusTimestamp,
                          ).toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                    {(messageStatus.status === "FAILED" ||
                      messageStatus.status === "REJECTED") &&
                      messageStatus.metaErrorCode && (
                        <div className="tw-mt-2 tw-p-2 tw-bg-red-50 tw-border tw-border-red-100 tw-rounded tw-text-xs">
                          <strong className="tw-text-red-700">
                            Error {messageStatus.metaErrorCode}:
                          </strong>
                          <span className="tw-text-red-600 tw-ml-1">
                            {messageStatus.metaErrorTitle}
                          </span>
                          {messageStatus.metaErrorDetails && (
                            <p className="tw-mt-1 tw-mb-0 tw-text-red-500">
                              {messageStatus.metaErrorDetails}
                            </p>
                          )}
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}
            <div className="tw-flex tw-justify-end tw-gap-2">
              <Button onClick={() => setIsTestModalVisible(false)}>
                Close
              </Button>
              <Button type="primary" htmlType="submit" loading={testLoading}>
                Send Test Message
              </Button>
            </div>
          </Form>
        </div>
      </Modal>

      <WhatsAppTemplateComposer
        open={isCreateModalVisible}
        visible={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        onClose={() => setIsCreateModalVisible(false)}
        onSuccess={() => {
          setIsCreateModalVisible(false);
          fetchTemplates();
        }}
      />

      <Modal
        title="Start New Chat"
        visible={isNewChatModalVisible}
        onCancel={() => {
          setIsNewChatModalVisible(false);
          setLeadSearchQuery("");
          setSelectedLeadId(null);
          setSelectedLead(null);
        }}
        onOk={handleStartNewChat}
        okText="Start Chat"
        okButtonProps={{ disabled: !selectedLeadId }}
        destroyOnClose
      >
        <div className="tw-mt-4">
          <p className="tw-text-sub-text tw-mb-4">
            Search for a lead to start a WhatsApp conversation. Only leads with
            a valid phone number will appear.
          </p>

          {localConnectionData?.phoneNumbers?.length > 1 && (
            <div className="tw-flex tw-flex-col tw-gap-2 tw-mb-6">
              <label className="tw-font-medium tw-text-sm tw-text-text-primary">
                Send From
              </label>
              <Select
                value={
                  newChatPhoneId ||
                  selectedPhoneId ||
                  localConnectionData?.phoneNumbers[0]?.id
                }
                onChange={(val) => setNewChatPhoneId(val)}
                className="tw-w-full"
                size="large"
              >
                {localConnectionData.phoneNumbers.map((acc) => (
                  <Option key={acc.id} value={acc.id}>
                    {acc.displayPhoneNumber}
                  </Option>
                ))}
              </Select>
            </div>
          )}

          <div className="tw-flex tw-flex-col tw-gap-2 tw-mb-6">
            <label className="tw-font-medium tw-text-sm tw-text-text-primary">
              Search Lead
            </label>
            <Select
              showSearch
              allowClear
              value={selectedLeadId || undefined}
              placeholder="Search by name, email, or phone..."
              defaultActiveFirstOption={false}
              filterOption={false}
              optionLabelProp="label"
              onSearch={(val) => setLeadSearchQuery(val)}
              onChange={(val) => {
                setSelectedLeadId(val);
                const lead = leadOptionsToRender.find((l) => l.id === val);
                setSelectedLead(lead || null);
              }}
              onClear={() => {
                setSelectedLeadId(null);
                setSelectedLead(null);
                setLeadSearchQuery("");
              }}
              notFoundContent={
                searchingLeads
                  ? "Searching..."
                  : leadSearchQuery
                    ? "No leads found with a phone number."
                    : "Type to search"
              }
              className="tw-w-full"
              size="large"
            >
              {leadOptionsToRender.map((lead) => {
                const leadName =
                  lead.fullname ||
                  `${lead.firstname || ""} ${lead.lastname || ""}`.trim() ||
                  "Lead";
                const leadPhone = lead.phoneNo || lead.mobile || "";
                return (
                  <Option
                    key={lead.id}
                    value={lead.id}
                    label={`${leadName}${leadPhone ? ` (${leadPhone})` : ""}`}
                  >
                    <div className="tw-flex tw-flex-col tw-py-1">
                      <span className="tw-font-medium tw-text-slate-800">
                        {leadName}
                      </span>
                      <span className="tw-text-xs tw-text-slate-500 tw-mt-0.5">
                        {leadPhone}
                        {lead.emailId ? ` • ${lead.emailId}` : ""}
                      </span>
                    </div>
                  </Option>
                );
              })}
            </Select>

            {selectedLead && (
              <div className="tw-mt-2.5 tw-p-3 tw-bg-slate-50 tw-rounded-lg tw-border tw-border-slate-200 tw-flex tw-items-center tw-justify-between">
                <div className="tw-flex tw-flex-col tw-gap-0.5">
                  <span className="tw-font-medium tw-text-sm tw-text-slate-800">
                    {selectedLead.fullname ||
                      `${selectedLead.firstname || ""} ${selectedLead.lastname || ""}`.trim() ||
                      selectedLead.name ||
                      "Lead"}
                  </span>
                  <span className="tw-text-xs tw-text-slate-500">
                    {selectedLead.phoneNo || selectedLead.mobile}
                    {selectedLead.emailId ? ` • ${selectedLead.emailId}` : ""}
                  </span>
                </div>
                <span className="tw-text-xs tw-font-medium tw-text-green-700 tw-bg-green-50 tw-px-2 tw-py-0.5 tw-rounded tw-border tw-border-green-200">
                  WhatsApp Available
                </span>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {showAddLeadModal && (
        <CreateNewLead
          openModal={true}
          setShowAddLeadModal={setShowAddLeadModal}
          initialValues={{
            mobile: activeChat?.phone,
            firstname:
              activeChat?.name && activeChat.name !== activeChat.phone
                ? activeChat.name.split(" ")[0]
                : "",
            lastname:
              activeChat?.name && activeChat.name.includes(" ")
                ? activeChat.name.split(" ").slice(1).join(" ")
                : "",
          }}
        />
      )}
    </div>
  );
};

export default WhatsAppInbox;
